/**
 * Binary min-heap of (int value, float key) pairs backed by growable typed arrays. Used by A* and
 * the supply Dijkstra, which run every tick — so no per-push object allocation. Ties are resolved
 * by the heap's internal order, which depends only on the push/pop sequence ⇒ deterministic.
 */
export class MinHeap {
  private keys = new Float64Array(256);
  private vals = new Int32Array(256);
  size = 0;

  clear(): void {
    this.size = 0;
  }

  push(value: number, key: number): void {
    if (this.size === this.keys.length) this.grow();
    let i = this.size++;
    const keys = this.keys;
    const vals = this.vals;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent] <= key) break;
      keys[i] = keys[parent];
      vals[i] = vals[parent];
      i = parent;
    }
    keys[i] = key;
    vals[i] = value;
  }

  /** Key of the minimum element (heap must be non-empty). */
  peekKey(): number {
    return this.keys[0];
  }

  /** Remove and return the value with the smallest key (heap must be non-empty). */
  pop(): number {
    const keys = this.keys;
    const vals = this.vals;
    const top = vals[0];
    const n = --this.size;
    if (n > 0) {
      const key = keys[n];
      const val = vals[n];
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= n) break;
        const r = l + 1;
        const c = r < n && keys[r] < keys[l] ? r : l;
        if (keys[c] >= key) break;
        keys[i] = keys[c];
        vals[i] = vals[c];
        i = c;
      }
      keys[i] = key;
      vals[i] = val;
    }
    return top;
  }

  private grow(): void {
    const keys = new Float64Array(this.keys.length * 2);
    keys.set(this.keys);
    const vals = new Int32Array(this.vals.length * 2);
    vals.set(this.vals);
    this.keys = keys;
    this.vals = vals;
  }
}
